import React, { useEffect, useState, useCallback } from 'react';
import { Alert, Typography, Space } from 'antd';
import { useOnboardingStore } from '../stores/onboarding-store';
import LocationDisplayCard, { LocationData } from './LocationDisplayCard';
import type { CustomerCategoryLocationDTO } from '@/shared/api/model';

const { Title, Paragraph, Text } = Typography;

export interface LocationSelectionFormProps {
  categoryLocations: CustomerCategoryLocationDTO[];
  selectedLocations: any[];
  onLocationsChange: (locations: any[]) => void;
  errors?: Record<string, string>;
}

const LocationSelectionForm: React.FC<LocationSelectionFormProps> = ({
  categoryLocations,
  selectedLocations,
  onLocationsChange,
  errors = {},
}) => {
  const { markStepCompleted, markStepIncomplete } = useOnboardingStore();
  const [locationData, setLocationData] = useState<Record<string, LocationData>>({});

  // Initialize location data from selected locations
  useEffect(() => {
    if (selectedLocations && selectedLocations.length > 0) {
      const initialData: Record<string, LocationData> = {};
      selectedLocations.forEach(loc => {
        if (loc.locationConfigName) {
          initialData[loc.locationConfigName] = loc;
        }
      });
      setLocationData(initialData);
    }
  }, []); // Only run once on mount

  // Handle location change for a specific configuration
  const handleLocationChange = useCallback((locationConfigName: string, data: LocationData) => {
    console.log('Location change for', locationConfigName, data);
    
    setLocationData(prev => ({
      ...prev,
      [locationConfigName]: data,
    }));
  }, []);

  // Handle location removal
  const handleLocationRemove = useCallback((locationConfigName: string) => {
    setLocationData(prev => {
      const updated = { ...prev };
      delete updated[locationConfigName];
      return updated;
    });
  }, []);

  // Check if all required locations are valid and update parent
  useEffect(() => {
    const locations = Object.values(locationData);
    
    // Check if all required locations have been filled
    const requiredLocations = categoryLocations.filter(loc => loc.isRequired);
    const filledRequiredLocations = requiredLocations.filter(reqLoc => {
      const data = locationData[reqLoc.locationConfigName!];
      return data && data.locationSelections && Object.keys(data.locationSelections).length > 0;
    });
    
    const allRequiredFilled = requiredLocations.length === filledRequiredLocations.length;
    
    // Update parent with location data
    onLocationsChange(locations);
    
    // Mark step as completed if all required locations are filled
    if (allRequiredFilled) {
      markStepCompleted(5); // Location step (adjust index if needed)
    } else {
      markStepIncomplete(5);
    }
  }, [locationData, categoryLocations, onLocationsChange, markStepCompleted, markStepIncomplete]);

  if (!categoryLocations || categoryLocations.length === 0) {
    return (
      <Alert
        message="No Location Requirements"
        description="No location requirements are configured for this customer category. The customer will be created without address information."
        type="info"
        showIcon
      />
    );
  }

  // Sort locations by display order and primary flag
  const sortedLocations = [...categoryLocations]
    .filter(loc => loc.isVisible !== false)
    .sort((a, b) => {
      // Primary locations first
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      // Then by display order
      return (a.displayOrder || 0) - (b.displayOrder || 0);
    });

  return (
    <div className="location-selection-form">
      <div className="location-selection-header" style={{ marginBottom: 24 }}>
        <Title level={4}>Address Information</Title>
        <Paragraph type="secondary">
          Please provide the required address information for the customer. 
          {sortedLocations.some(loc => loc.isRequired) && (
            <Text strong style={{ display: 'block', marginTop: 8 }}>
              * Required fields must be completed to proceed.
            </Text>
          )}
        </Paragraph>
      </div>

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {sortedLocations.map((locationConfig) => (
          <LocationDisplayCard
            key={locationConfig.locationConfigName}
            locationConfig={locationConfig}
            selectedLocation={locationData[locationConfig.locationConfigName!]}
            onLocationChange={(data) => handleLocationChange(locationConfig.locationConfigName!, data)}
            onLocationRemove={
              !locationConfig.isRequired 
                ? () => handleLocationRemove(locationConfig.locationConfigName!)
                : undefined
            }
            disabled={false}
          />
        ))}
      </Space>

      {errors.locations && (
        <Alert
          message={errors.locations}
          type="error"
          showIcon
          style={{ marginTop: 24 }}
        />
      )}
    </div>
  );
};

export default LocationSelectionForm;
